import type { FC, FormEvent } from 'react';
import React, { useEffect, useState } from 'react';
import { groupsApi } from 'src/shared/api/groupsApi';
import { setError, setSuccess } from 'src/shared/lib/utils';
import type { GroupUser } from 'src/shared/types/groups';
import { Button, Modal } from 'src/shared/ui';

import { validateSelection } from '../lib/validation';

const nativeFieldClassName =
  'font-gothampro-400 h-11 w-full rounded border border-violet-500 bg-white px-4 text-black ' +
  'transition-all focus:border-blue-100 focus:outline-none';

const labelClassName = 'font-gothampro-400 mb-1 ml-1 text-xs leading-3 text-grey-300';
const errorClassName = 'font-gothampro-400 mt-1 text-extra-xs text-pink-100';

interface MembersTabProps {
  groupId: number;
  members: GroupUser[];
  onUpdate: () => void | Promise<void>;
}

export const MembersTab: FC<MembersTabProps> = ({ groupId, members, onUpdate }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [users, setUsers] = useState<GroupUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectionError, setSelectionError] = useState('');

  const fetchUsers = async () => {
    try {
      const { data } = await groupsApi.getUsers();
      setUsers(data);
    } catch (error) {
      setError('Ошибка при загрузке пользователей');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedUserId('');
    setSelectionError('');
  };

  const handleAddMember = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = validateSelection(selectedUserId, 'Выберите пользователя');
    setSelectionError(error);

    if (error) {
      return;
    }

    try {
      await groupsApi.addGroupMember(groupId, Number(selectedUserId));
      setSuccess('Участник добавлен успешно');
      await onUpdate();
      closeModal();
    } catch (error) {
      setError('Ошибка при добавлении участника');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    try {
      await groupsApi.removeGroupMember(groupId, userId);
      setSuccess('Участник удален успешно');
      await onUpdate();
    } catch (error) {
      setError('Ошибка при удалении участника');
    }
  };

  return (
    <div className="flex flex-col">
      <div className="border-b p-4">
        <Button classNames="w-auto" onClick={() => setIsModalVisible(true)} variant="green">
          Добавить участника
        </Button>
      </div>

      <div className="overflow-auto p-4">
        <table className="min-w-full divide-y divide-grey-1200">
          <thead className="bg-grey-1100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Имя</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Логин</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Email</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Роль</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-1200 bg-white">
            {members.length ? (
              members.map(member => (
                <tr key={member.id}>
                  <td className="px-4 py-3 text-sm text-black">{member.full_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-black">{member.user_name}</td>
                  <td className="px-4 py-3 text-sm text-black">{member.email}</td>
                  <td className="px-4 py-3 text-sm text-black">{member.role}</td>
                  <td className="px-4 py-3">
                    <Button
                      classNames="w-auto px-4"
                      onClick={() => handleRemoveMember(member.id)}
                      size="small"
                      variant="orange"
                    >
                      Удалить
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-4 py-6 text-center text-sm text-grey-300" colSpan={5}>
                  В группе пока нет участников
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalVisible && (
        <Modal classNames="z-130 bg-black/40 px-4" onClose={closeModal}>
          <div className="w-[min(90vw,30rem)]">
            <h2 className="mb-6 text-xl font-semibold text-black">Добавить участника</h2>

            <form className="flex flex-col gap-4" onSubmit={handleAddMember}>
              <div>
                <div className={labelClassName}>Пользователь</div>
                <select
                  className={nativeFieldClassName}
                  onChange={event => {
                    setSelectedUserId(event.target.value);
                    setSelectionError('');
                  }}
                  value={selectedUserId}
                >
                  <option value="">Выберите пользователя</option>
                  {users.map(user => (
                    <option key={user.id} value={String(user.id)}>
                      {user.full_name || user.user_name} ({user.email})
                    </option>
                  ))}
                </select>
                {selectionError && <div className={errorClassName}>{selectionError}</div>}
              </div>

              <div className="mt-2 flex justify-end gap-3">
                <Button classNames="w-auto px-5" onClick={closeModal} type="button" variant="grey">
                  Отмена
                </Button>
                <Button classNames="w-auto px-5" type="submit" variant="green">
                  Добавить
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};
